import {GridSortModel} from '@mui/x-data-grid';
import {v4} from 'uuid';
import {TFunction} from 'i18next';
import {ColumnConfig} from '../../config/reportTypes';

// Constants
export const STRING_PATH = 'reports.reviewProgressAssignment.';
export const INITIAL_PAGE_SIZE = 3;
export const PAGE_SIZE_INCREMENT = 3;
// Hidden columns by default: Responsive Docs, Privileged Docs, etc.
// All other columns (0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12, 16, 17) are selected by default
export const DEFAULT_HIDDEN_COLUMNS = [5, 13, 14, 15, 18, 19, 20, 21, 22];

// Types
export interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}

export interface SavedReportData {
  reportId: number;
  notificationId?: number;
  reportFilePath?: string;
  reportName?: string;
  isLoadMore?: boolean;
}

export interface SaveReportPayload {
  reportId: number;
  selectedCustomColumns: number[] | null; // null = not set yet, [] = all visible, [x,y] = hidden columns
  [key: string]: any;
}

// Column Definitions
export const getBaseColumns = (t: TFunction): ColumnConfig[] => {
  const stringPath = STRING_PATH;
  return [
    {
      field: 'assignment_Name',
      headerName: t(stringPath + 'assignment'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 0,
    },
    {
      field: 'assignment_Path',
      headerName: t(stringPath + 'assignmentPath'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 1,
    },
    {
      field: 'assignmentLevel',
      headerName: t(stringPath + 'assignmentLevel'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 2,
    },
    {
      field: 'totalDOcs',
      headerName: t(stringPath + 'totalDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 3,
    },
    {
      field: 'assignedDocs',
      headerName: t(stringPath + 'assignedDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 4,
    },
    {
      field: 'unAssignedDocs',
      headerName: t(stringPath + 'docsNotDistributed'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 5,
    },
    {
      field: 'completedDocs',
      headerName: t(stringPath + 'completedDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 6,
    },
    {
      field: 'remainingDocs',
      headerName: t(stringPath + 'remainingDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 7,
    },
    {
      field: 'completedPercentage',
      headerName: t(stringPath + 'completedPercentage'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 8,
    },
    {
      field: 'totalAudioDocs',
      headerName: t(stringPath + 'totalAudioDocsInAssignment'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 9,
    },
    {
      field: 'totalAudioHours',
      headerName: t(stringPath + 'totalAudioHoursInAssignment'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 10,
    },
    {
      field: 'audioDocsCompleted',
      headerName: t(stringPath + 'totalAudioDocsCompleted'),
      flex: 1,
      minWidth: 180,
      isVisible: true,
      index: 11,
    },
    {
      field: 'audioHoursCompleted',
      headerName: t(stringPath + 'totalAudioHoursCompleted'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 12,
    },
    {
      field: 'totalChatDocs',
      headerName: t(stringPath + 'totalChatDocs'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 13,
    },
    {
      field: 'chatDocsCompleted',
      headerName: t(stringPath + 'completedChatDocs'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 14,
    },
    {
      field: 'totalChatDuration',
      headerName: t(stringPath + 'totalChatDuration'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 15,
    },
    {
      field: 'totalReviewTimeSpent',
      headerName: t(stringPath + 'estimatedTotalReviewTimeSpent'),
      flex: 1,
      minWidth: 250,
      isVisible: true,
      index: 16,
    },
    {
      field: 'estimatedRemainingTime',
      headerName: t(stringPath + 'estimatedRemainingTime'),
      flex: 1,
      minWidth: 220,
      isVisible: true,
      index: 17,
    },
    {
      field: 'totalNoofReviewers',
      headerName: t(stringPath + 'totalNumOfReviewers'),
      flex: 1,
      minWidth: 180,
      isVisible: true,
      index: 18,
    },
    {
      field: 'noofReviewersCompleted',
      headerName: t(stringPath + 'numOfReviewersCompleted'),
      flex: 1,
      minWidth: 200,
      isVisible: true,
      index: 19,
    },
    {
      field: 'noofReviewersNotCompleted',
      headerName: t(stringPath + 'numOfReviewersNotCompleted'),
      flex: 1,
      minWidth: 220,
      isVisible: true,
      index: 20,
    },
    {
      field: 'responsive',
      headerName: t(stringPath + 'responsiveDocs'),
      flex: 1,
      minWidth: 130,
      isVisible: true,
      index: 21,
    },
    {
      field: 'previlaged',
      headerName: t(stringPath + 'privilegedDocs'),
      flex: 1,
      minWidth: 130,
      isVisible: true,
      index: 22,
    },
  ];
};

export const getSummaryColumns = (t: TFunction): ColumnConfig[] => {
  const stringPath = STRING_PATH;
  return [
    {
      field: 'totalDOcs',
      headerName: t(stringPath + 'totalDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 23,
    },
    {
      field: 'assignedDocs',
      headerName: t(stringPath + 'assignedDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 24,
    },
    {
      field: 'completedDocs',
      headerName: t(stringPath + 'completedDocs'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 25,
    },
    {
      field: 'remainingDocs',
      headerName: t(stringPath + 'remaining'),
      flex: 1,
      minWidth: 120,
      isVisible: true,
      index: 26,
    },
    {
      field: 'completedPercentage',
      headerName: t(stringPath + 'completedPercentage'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 27,
    },
    {
      field: 'totalReviewTimeSpent',
      headerName: t(stringPath + 'estimatedTotalReviewTimeSpent'),
      flex: 1,
      minWidth: 250,
      isVisible: true,
      index: 28,
    },
    {
      field: 'estimatedRemainingTime',
      headerName: t(stringPath + 'estimatedRemainingTime'),
      flex: 1,
      minWidth: 220,
      isVisible: true,
      index: 29,
    },
    {
      field: 'previlaged',
      headerName: t(stringPath + 'privileged'),
      flex: 1,
      minWidth: 100,
      isVisible: true,
      index: 30,
    },
    {
      field: 'responsive',
      headerName: t(stringPath + 'responsive'),
      flex: 1,
      minWidth: 100,
      isVisible: true,
      index: 31,
    },
    {
      field: 'assignmentLevel',
      headerName: t(stringPath + 'assignmentLevel'),
      flex: 1,
      minWidth: 150,
      isVisible: true,
      index: 32,
    },
  ];
};

// Data Transformation
export interface TransformReviewReportDataParams {
  response: any;
  isLoadMore?: boolean;
  accumulatedAssignmentProgressList: any[];
  setAccumulatedAssignmentProgressList: (data: any[]) => void;
  setRawApiResponse: (data: any) => void;
  setCurrentPageSize: (size: number) => void;
}

export const transformReviewReportData = ({
  response,
  isLoadMore = false,
  accumulatedAssignmentProgressList,
  setAccumulatedAssignmentProgressList,
  setRawApiResponse,
  setCurrentPageSize,
}: TransformReviewReportDataParams): any[] => {
  const gridData: any[] = [];
  let newAssignmentProgressList: any[] = [];

  if (
    response?.assignmentProgressList &&
    Array.isArray(response.assignmentProgressList)
  ) {
    newAssignmentProgressList = response.assignmentProgressList;

    // Accumulate assignment progress list
    let accumulatedData: any[];
    if (isLoadMore) {
      // When loading more, API returns all items up to currentPageSize
      // We need to extract only the new items (items after the ones we already have)
      const existingCount = accumulatedAssignmentProgressList.length;
      const newItems = newAssignmentProgressList.slice(existingCount);
      accumulatedData = [...accumulatedAssignmentProgressList, ...newItems];
      setAccumulatedAssignmentProgressList(accumulatedData);
    } else {
      accumulatedData = newAssignmentProgressList;
      setAccumulatedAssignmentProgressList(newAssignmentProgressList);
      // When initially loading data (not load more), set currentPageSize to match the actual data count
      // This ensures Load More works correctly when coming from saved reports
      if (accumulatedData.length > 0) {
        setCurrentPageSize(accumulatedData.length);
      }
    }

    // Update raw API response with accumulated data
    setRawApiResponse({
      ...response,
      assignmentProgressList: accumulatedData,
    });

    // Transform to grid data
    accumulatedData.forEach((progress: any) => {
      // Add assignment group items
      if (
        progress.reviewProgress_AssignmentGroup_List &&
        Array.isArray(progress.reviewProgress_AssignmentGroup_List)
      ) {
        progress.reviewProgress_AssignmentGroup_List.forEach((item: any) => {
          gridData.push({
            id: `${v4()}_${item.assignmentID || item.assignmentsID}`,
            ...item,
          });
        });
      }
      // Add assignment details if include_Assignment_Detail is enabled
      if (
        progress.assignmentDetailsList &&
        Array.isArray(progress.assignmentDetailsList)
      ) {
        progress.assignmentDetailsList.forEach((item: any) => {
          gridData.push({
            id: `${v4()}_${item.assignmentID || item.assignmentsID}`,
            ...item,
          });
        });
      }
    });
  }
  return gridData;
};

// URL Parsing Utilities
export const getReportIdFromUrl = (
  pathname: string,
  search: string
): number => {
  const key = pathname.split('/')[2];
  if (key === 'assign-report' || key === 'AssignmentProgressReport') {
    const params = new URLSearchParams(search);
    const id =
      params.get('ReportID') ??
      params.get('reportId') ??
      params.get('ReportId') ??
      params.get('reportID') ??
      '';
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const hasReportLabelInUrl = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.get('ReportLabel') !== null;
};

export const getReportKey = (pathname: string): string => {
  const key = pathname.split('/')[2];
  if (key === 'assign-report' || key === 'AssignmentProgressReport') {
    return 'ReviewProgressAssignmentReport';
  }
  return key;
};

// Column Visibility Utilities
export const applyColumnVisibility = (
  baseCols: ColumnConfig[],
  hiddenIndexes: number[]
): ColumnConfig[] => {
  const hiddenSet = new Set(hiddenIndexes ?? []);
  return (baseCols ?? []).map((col: ColumnConfig) => {
    const idx = col?.index;
    if (typeof idx === 'number' && hiddenSet.has(idx)) {
      return {...col, isVisible: false};
    }
    return col;
  });
};

export const calculateEffectiveSelectedCustomColumns = (
  saveReportPayloadSelectedCustomColumns: number[] | null,
  effectiveSavedReportDataSelectedCustomColumns: number[] | undefined,
  rawApiResponseSourceColumnList: number[] | undefined
): number[] => {
  // Priority: user selection (even if empty []) > saved data > API sourceColumnList > DEFAULT_HIDDEN_COLUMNS
  // null means no user selection yet, [] means user explicitly selected all columns (no hidden)
  const raw =
    saveReportPayloadSelectedCustomColumns !== null
      ? saveReportPayloadSelectedCustomColumns // User has made a selection (could be [] for all visible)
      : (effectiveSavedReportDataSelectedCustomColumns?.length ?? 0) > 0
        ? effectiveSavedReportDataSelectedCustomColumns
        : Array.isArray(rawApiResponseSourceColumnList) &&
            rawApiResponseSourceColumnList.length > 0
          ? rawApiResponseSourceColumnList
          : DEFAULT_HIDDEN_COLUMNS; // Use DEFAULT_HIDDEN_COLUMNS when nothing is selected (first time / not selected from dropdown)
  // Normalize: remove duplicates
  const base = Array.isArray(raw) ? raw : [];
  const nums = base
    .map((x: any) => Number(x))
    .filter((n: any) => Number.isFinite(n))
    .map((n: number) => Math.trunc(n));
  return Array.from(new Set(nums));
};

// State Management Utilities
export const createInitialPaginationSortConfig = (
  getDefaultSorting: () => GridSortModel
): PageSortConfigType => ({
  pageSize: INITIAL_PAGE_SIZE,
  pageNumber: 1,
  sort: getDefaultSorting(),
});

export const createInitialSavedReportData = (): SavedReportData => ({
  reportId: 0,
});

export const createInitialSaveReportPayload = (): SaveReportPayload => ({
  reportId: 0,
  selectedCustomColumns: null, // null means no user selection yet
});

